import React, { useState, useRef, useEffect } from 'react';
import { Send, Image, Paperclip, XCircle, Mic } from 'lucide-react';
import { Theme } from '../types';
import './ChatInput.css';

interface FileMetadata {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  content: string;
}

interface ChatInputProps {
  onSend: (content: string, images: string[], files: FileMetadata[]) => boolean;
  theme: Theme;
  sendKey: string; // New prop for send key preference
  isLoading: boolean; // New prop to indicate loading state
  onCancel: () => void; // New prop for cancel action
  inputRef?: React.RefObject<HTMLTextAreaElement>; // Optional ref for external focus control
  disableImageUpload?: boolean; // Optional prop to disable image upload (for AWS)
}

export function ChatInput({ 
  onSend, 
  sendKey, 
  isLoading, 
  onCancel, 
  inputRef, 
  disableImageUpload = false,
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [lastSpeechTime, setLastSpeechTime] = useState<number | null>(null);
  const [silenceTimeoutId, setSilenceTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [hasSpeechContent, setHasSpeechContent] = useState(false);
  const [continuousMode, setContinuousMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const localTextareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Use the provided inputRef if available, otherwise use our local ref
  const textareaRef = inputRef || localTextareaRef;
  
  // Track the previous transcript to handle Android word duplication
  const previousTranscripts = useRef<{[key: number]: string}>({});
  const isAndroid = useRef<boolean>(navigator.userAgent.toLowerCase().includes('android'));
  // Keep track of all final results to detect exact duplicates
  const allFinalResults = useRef<string[]>([]);
  
  // Function to reset transcript tracking
  const resetTranscriptTracking = () => {
    previousTranscripts.current = {};
    allFinalResults.current = [];
    console.log('🎤 Android fix: Reset transcript tracking');
  };
  
  // Debug mode - can be enabled by tapping mic button 5 times quickly or via localStorage
  const [debugMode, setDebugMode] = useState<boolean>(false);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const maxDebugLines = 30;
  const tapTimestamps = useRef<number[]>([]);
  
  // Check localStorage on component mount to see if debug mode is enabled
  useEffect(() => {
    try {
      // Check for stored debug preference
      const debugEnabled = localStorage.getItem('interpretosVoiceDebug') === 'true';
      
      // Also check URL for debug param (easier for testing)
      const urlParams = new URLSearchParams(window.location.search);
      const debugParam = urlParams.get('voiceDebug');
      
      if (debugEnabled || debugParam === 'true') {
        setDebugMode(true);
        console.log('🎤 Voice debug mode activated via', debugEnabled ? 'localStorage' : 'URL parameter');
      }
    } catch (e) {
      // Ignore localStorage errors
    }
  }, []);
  
  // Function to track mic button taps for debug mode activation
  const handleMicTap = () => {
    const now = Date.now();
    tapTimestamps.current.push(now);
    
    // Keep only last 5 taps
    if (tapTimestamps.current.length > 5) {
      tapTimestamps.current = tapTimestamps.current.slice(-5);
    }
    
    // If we have 5 taps within 3 seconds, toggle debug mode
    if (tapTimestamps.current.length === 5) {
      const earliest = tapTimestamps.current[0];
      if (now - earliest < 3000) {
        const newDebugMode = !debugMode;
        setDebugMode(newDebugMode);
        
        // Store in localStorage to persist across refreshes
        try {
          localStorage.setItem('interpretosVoiceDebug', newDebugMode.toString());
        } catch (e) {
          // Ignore localStorage errors
        }
        
        // Clear tap history
        tapTimestamps.current = [];
        
        // Add status message
        if (newDebugMode) {
          alert('Voice recognition debug mode activated');
          console.log('🎤 Voice debug mode activated');
        } else {
          alert('Voice recognition debug mode deactivated');
          console.log('🎤 Voice debug mode deactivated');
        }
      }
    }
  };
  
  const addDebugMessage = (message: string) => {
    if (debugMode) {
      setDebugInfo(prev => {
        const newMessages = [...prev, `${new Date().toISOString().substr(11, 8)}: ${message}`];
        // Keep only the last several messages
        return newMessages.slice(-maxDebugLines);
      });
    }
    // Always log to console for developer debugging
    console.log(`🎤 DEBUG: ${message}`);
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);
  
  // Add click outside handler for attachment menu
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          event.target && !(event.target as Element).closest('button[title="Add content"]')) {
        setAttachMenuOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 300)}px`; // Set max height to 300px
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('📝 Form submission triggered');
    e.preventDefault();
    console.log('📝 Default prevented on form submit event');
    
    // Prevent duplicate submissions
    if (isSubmitting) {
      console.log('📝 Submission already in progress, ignoring');
      return;
    }
    
    // Get text directly from textarea for most current value
    const currentText = textareaRef.current?.value || message;
    
    // Check if we have content to send
    if (currentText.trim() || images.length > 0 || files.length > 0) {
      console.log('📝 Content available, sending message');
      console.log('📝 Message from textarea:', currentText);
      console.log('📝 Message from state:', message);
      console.log('📝 Images:', images.length);
      console.log('📝 Files:', files.length);
      
      // Mark as submitting to prevent duplicates
      setIsSubmitting(true);
      
      try {
        // Send the message using text from textarea
        console.log('📝 Calling onSend with:', currentText);
        const success = await onSend(currentText, images, files);
        console.log('📝 onSend returned:', success);
        if (!success) {
          console.log('📝 onSend returned false, not clearing inputs');
          return;
        }
        console.log('📝 onSend called successfully');
        
        // Clear input fields
        setMessage('');
        if (textareaRef.current) {
          textareaRef.current.value = '';
        }
        setImages([]);
        setFiles([]);
        console.log('📝 Input fields cleared');
        
        // Also make sure voice recognition is stopped
        stopSpeechRecognition();
        
        // If continuous mode is enabled, restart the microphone after a short delay
        if (continuousMode) {
          console.log('🔄 Continuous mode active, will restart microphone in 1 second');
          setTimeout(() => {
            console.log('🔄 Restarting microphone for continuous conversation');
            startSpeechRecognition();
          }, 1000);
        }
        
        // Reset submitting state after a delay
        setTimeout(() => {
          setIsSubmitting(false);
        }, 2000);
      } catch (error) {
        console.error('📝 Error sending message:', error);
        console.error('📝 Error details:', error.message, error.stack);
        setIsSubmitting(false);
      }
    } else {
      console.log('📝 No content to send');
    }
  };

  // Simple direct submit function that bypasses form handling
  const directSubmit = (text: string) => {
    console.log('🔥 Direct submit called with:', text);
    
    // Prevent duplicate submissions
    if (isSubmitting) {
      console.log('🔥 Submission already in progress, ignoring direct submit');
      return;
    }
    
    if (text.trim() || images.length > 0 || files.length > 0) {
      // Mark as submitting to prevent duplicates
      setIsSubmitting(true);
      
      try {
        // Call onSend directly with the current text
        onSend(text, images, files);
        console.log('🔥 Message directly sent via onSend');
        
        // Clear inputs
        setMessage('');
        if (textareaRef.current) {
          textareaRef.current.value = '';
        }
        setImages([]);
        setFiles([]);
        
        // If continuous mode is enabled, restart the microphone after a short delay
        // to allow the response to be processed and displayed
        if (continuousMode) {
          console.log('🔄 Continuous mode active, will restart microphone in 1 second');
          setTimeout(() => {
            console.log('🔄 Restarting microphone for continuous conversation');
            startSpeechRecognition();
          }, 1000);
        }
        
        // Reset submitting state after a delay
        setTimeout(() => {
          setIsSubmitting(false);
        }, 2000);
      } catch (error) {
        console.error('🔥 Error in directSubmit:', error);
        setIsSubmitting(false);
      }
    } else {
      console.log('🔥 No content to submit in directSubmit');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (sendKey === 'Enter') {
      if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey || e.shiftKey)) {
      }
    } else {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit(e);
      } else if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
      }
    }
  };

  const handleImagePaste = (e: React.ClipboardEvent) => {
    // Always allow image pasting regardless of connection type
    const items = e.clipboardData?.items;
    if (items) {
      for (const item of Array.from(items)) {
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
              setImages((prev) => [...prev, e.target?.result as string]);
            };
            reader.readAsDataURL(file);
          }
        }
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const createFileMetadata = (file: File, content: string): FileMetadata => {
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      content: content
    };
  };

  const readFileContent = (file: File): Promise<FileMetadata> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result as string;
        resolve(createFileMetadata(file, content));
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    
    try {
      const filesMetadata = await Promise.all(
        Array.from(selectedFiles).map(readFileContent)
      );
      setFiles(prev => [...prev, ...filesMetadata]);
    } catch (error) {
      console.error('Error reading file content:', error);
      // You might want to show an error message to the user here
    }
  };

  // Speech recognition functionality 
  const toggleSpeechRecognition = () => {
    if (!isListening) {
      startSpeechRecognition();
    } else {
      stopSpeechRecognition();
    }
  };

  
  // Function to start silence detection with increased protection against duplicates
  const startSilenceDetection = () => {
    console.log('🕒 Starting silence detection...');
    
    // Clear any existing timeout
    if (silenceTimeoutId) {
      clearTimeout(silenceTimeoutId);
    }
    
    // Set a new timeout to check for silence
    const timeoutId = setTimeout(() => {
      console.log('🕒 Silence detected! (' + (Date.now() - (lastSpeechTime || 0)) + 'ms)');
      
      // Get the most current message value directly from the DOM to ensure we have the latest
      const currentMessage = textareaRef.current?.value || message;
      console.log('🕒 Current message value:', currentMessage);
      
      // Store the current message value to check for changes
      const messageAtTimeOfSubmission = currentMessage;
      
      // Only submit if we have content AND we're not already submitting
      if (currentMessage.trim() && !isSubmitting) {
        console.log('🕒 Auto-submitting message after silence');
        
        // Mark that we're submitting to prevent duplicates
        setIsSubmitting(true);
        
        // First completely stop voice recognition to avoid additional transcripts coming in
        stopSpeechRecognition();
        console.log('🕒 Stopped speech recognition');
        
        // Then wait a bit to ensure no more transcripts are being processed
        setTimeout(() => {
          // Get the latest message again to make sure nothing changed during the delay
          const finalMessage = textareaRef.current?.value || message;
          
          // Double-check if message changed during the delay (which would indicate more recognition events came in)
          if (finalMessage !== messageAtTimeOfSubmission) {
            console.log('🕒 Message changed during delay, waiting for next silence period');
            setIsSubmitting(false);
            if (isListening) {
              // Re-start silence detection with the new content
              startSilenceDetection();
            }
            return;
          }
          
          // Call directSubmit to bypass form event mechanism
          directSubmit(finalMessage);
          console.log('🕒 Direct submission called with message:', finalMessage);
        }, 300); // Increased delay to ensure recognition is fully stopped
      } else {
        console.log('🕒 Not submitting - ' + (isSubmitting ? 'already submitting' : 'no content in message field'));
      }
    }, 2500); // Increased to 2.5 seconds of silence before submission
    
    setSilenceTimeoutId(timeoutId);
  };
  
  const startSpeechRecognition = () => {
    console.log('🎤 Starting speech recognition...');
    
    // Reset the Android transcript tracking at the start of a new session
    resetTranscriptTracking();
    
    // If already listening in continuous mode, don't restart unnecessarily
    if (isListening && continuousMode) {
      console.log('🎤 Already listening in continuous mode, not restarting');
      return;
    }
    
    // Reset speech tracking state
    setHasSpeechContent(false);
    setLastSpeechTime(null);
    
    // Clear any existing timeouts
    if (silenceTimeoutId) {
      clearTimeout(silenceTimeoutId);
      setSilenceTimeoutId(null);
    }
    
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      console.log('🎤 Speech recognition is supported in this browser');
      
      // @ts-ignore - TypeScript doesn't know about the Speech API
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      // Configure recognition - use continuous mode to keep listening
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      // Special Android configuration for better performance
      if (isAndroid?.current) {
        // Limit max alternatives to reduce duplicates on Android
        recognition.maxAlternatives = 1;
        
        // Add Android specific settings here if needed in the future
        console.log('🎤 Android-specific recognition settings applied');
      }
      
      console.log('🎤 Speech recognition configured:', { 
        continuous: recognition.continuous,
        interimResults: recognition.interimResults,
        lang: recognition.lang,
        maxAlternatives: recognition.maxAlternatives,
        isAndroid: isAndroid?.current
      });
      
      // For Android, always clear the message when starting speech recognition
      // This prevents accumulation across multiple voice recognition sessions
      if (isAndroid?.current) {
        addDebugMessage('Starting speech - clearing input on Android');
        setMessage('');
        if (textareaRef.current) {
          textareaRef.current.value = '';
        }
      } 
      // For other platforms, only clear if empty
      else if (!message.trim()) {
        setMessage('');
      }
      
      recognition.onstart = () => {
        console.log('🎤 Speech recognition started');
        console.log('🎤 USAGE: Speak your message, then pause for 2 seconds to auto-submit');
        setIsListening(true);
      };
      
      recognition.onresult = (event: any) => {
        console.log('🎤 Speech recognition result received:', event.results);
        console.log('🎤 Is Android device:', isAndroid?.current);
        addDebugMessage(`Android=${isAndroid?.current}, Results=${event.results.length}`);
        // Special Android debug - set flags directly in browser localStorage for debugging
        if (isAndroid?.current) {
          try {
            localStorage.setItem('androidDebug', JSON.stringify({
              userAgent: navigator.userAgent,
              time: new Date().toISOString(),
              resultsCount: event.results.length
            }));
          } catch (e) {
            // Ignore storage errors
          }
        }
        
        // Update the last speech time whenever we get a result
        setLastSpeechTime(Date.now());
        
        // Process all results in the event
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const isFinal = result.isFinal;
          const transcript = result[0].transcript;
          
          console.log('🎤 Result index:', i, 'Final:', isFinal);
          console.log('🎤 Transcript:', transcript);
          console.log('🎤 Confidence:', result[0].confidence);
          
          addDebugMessage(`Result[${i}]: ${isFinal ? "FINAL" : "interim"} "${transcript.substr(0, 15)}..."`);
          
          // Store raw result data for debugging
          if (isAndroid?.current) {
            try {
              const debugData = JSON.parse(localStorage.getItem('androidDebugResults') || '[]');
              debugData.push({
                index: i,
                time: new Date().toISOString(),
                isFinal,
                transcript: transcript.substr(0, 50) + (transcript.length > 50 ? '...' : '')
              });
              // Keep only last 20 results to avoid storage limits
              if (debugData.length > 20) {
                debugData.shift();
              }
              localStorage.setItem('androidDebugResults', JSON.stringify(debugData));
            } catch (e) {
              // Ignore storage errors
            }
          }
          
          // SPECIAL CASE: We use the allFinalResults ref defined at component level
          
          if (isFinal) {
            // This is a final result, add to message
            console.log('🎤 Adding final transcript to message:', transcript);
            
            // For Android, handle word repetition bug with enhanced algorithm
            let cleanedTranscript = transcript.trim();
            let skipThisResult = false;
            
            if (isAndroid?.current) {
              // Add this result to our tracking array
              if (transcript.trim()) {
                allFinalResults.current.push(transcript.trim());
                addDebugMessage(`Result added to tracking (${allFinalResults.current.length} total)`);
              }
              
              // Get the previous transcript for this result index
              const prevTranscript = previousTranscripts.current[i];
              
              // ANDROID BUG FIX 1: Exact duplication detection - if we've seen this exact
              // transcript before, skip it entirely
              const exactDuplicateCount = allFinalResults.current.filter(
                t => t === transcript.trim()
              ).length;
              
              // Add debug info to show exactly what's happening with duplicates
              addDebugMessage(`Duplicate check: "${transcript.trim()}" appears ${exactDuplicateCount} times`);
              
              if (exactDuplicateCount > 1) {
                // We've seen this exact result before, skip it entirely
                addDebugMessage(`SKIP: Exact duplicate #${exactDuplicateCount}`);
                skipThisResult = true;
              }
              // Only proceed with additional checks if not already skipping
              else if (prevTranscript) {
                addDebugMessage(`Compare: new="${transcript.substr(0, 15)}..." vs prev="${prevTranscript.substr(0, 15)}..."`);
                
                // SPECIAL HANDLING for Android progressive accumulation bug
                // Android keeps creating new, longer transcripts that include repetitions
                // of the same phrase at the beginning
                
                // Method 1: Use the longest non-repeating substring algorithm
                const words = transcript.trim().split(/\s+/);
                const sequences = [];
                
                // Find all sequences of non-repeating words
                let currentSequence = [];
                const seenWords = new Set();
                
                for (let j = 0; j < words.length; j++) {
                  const word = words[j].toLowerCase();
                  if (!seenWords.has(word)) {
                    seenWords.add(word);
                    currentSequence.push(words[j]);
                  } else {
                    // We found a repeat - store this sequence and start a new one
                    if (currentSequence.length > 0) {
                      sequences.push(currentSequence.join(' '));
                    }
                    currentSequence = [words[j]];
                    seenWords.clear();
                    seenWords.add(word);
                  }
                }
                
                // Add the final sequence
                if (currentSequence.length > 0) {
                  sequences.push(currentSequence.join(' '));
                }
                
                // Choose the longest sequence
                if (sequences.length > 0) {
                  // Join with spaces if there are multiple sequences
                  const longestSequence = sequences.reduce((longest, current) => 
                    current.length > longest.length ? current : longest
                  , sequences[0]);
                  
                  addDebugMessage(`Special: Found ${sequences.length} sequences`);
                  cleanedTranscript = longestSequence;
                  addDebugMessage(`Fixed: Using longest sequence: "${cleanedTranscript.substr(0, 30)}..."`);
                } 
                else {
                  // Method 2: Check if new transcript is exactly the same as the previous one
                  if (transcript.trim() === prevTranscript.trim()) {
                    // Skip this duplicate result
                    addDebugMessage(`SKIP: Same as previous`);
                    skipThisResult = true;
                  }
                  // Method 3: Check if new transcript contains previous one 
                  else if (transcript.includes(prevTranscript)) {
                    // Only use the part after the previous transcript
                    const newPart = transcript.substring(prevTranscript.length).trim();
                    if (newPart) {
                      cleanedTranscript = newPart;
                      console.log('🎤 Android fix (M3): Detected repeated words');
                      addDebugMessage(`M3 fixed: "${cleanedTranscript.substr(0, 20)}..."`);
                    }
                  }
                }
              }
              
              // Update the previous transcript for this result index
              previousTranscripts.current[i] = transcript;
            }
            
            // If our Android-specific skip flag is set, don't process this result at all
            if (skipThisResult) {
              addDebugMessage(`SKIPPED result entirely`);
              return; // Skip processing this result
            }
            
            // FINAL PROTECTION LAYER: Check for a large number of duplicate words
            // Even after our previous fixes, we sometimes still get odd results
            // This catches those cases by filtering out repeated words
            if (isAndroid?.current) {
              const allWords = cleanedTranscript.split(/\s+/);
              if (allWords.length > 3) {
                const wordFrequency: {[key: string]: number} = {};
                
                // Count word frequency
                allWords.forEach(word => {
                  const normalizedWord = word.toLowerCase();
                  wordFrequency[normalizedWord] = (wordFrequency[normalizedWord] || 0) + 1;
                });
                
                // Find words that appear multiple times
                const repeatedWords = Object.entries(wordFrequency)
                  .filter(([_, count]) => count > 1)
                  .map(([word]) => word);
                
                if (repeatedWords.length > 0) {
                  addDebugMessage(`Found ${repeatedWords.length} repeated words`);
                  
                  // Build a clean version without excess repetition
                  const seenWords = new Set<string>();
                  const cleanWords = allWords.filter(word => {
                    const normalized = word.toLowerCase();
                    // If it's a repeated word and we've seen it before, filter it out
                    if (repeatedWords.includes(normalized) && seenWords.has(normalized)) {
                      return false;
                    }
                    seenWords.add(normalized);
                    return true;
                  });
                  
                  // Update if we actually removed anything
                  if (cleanWords.length < allWords.length) {
                    const oldText = cleanedTranscript;
                    cleanedTranscript = cleanWords.join(' ');
                    addDebugMessage(`Removed duplicates: "${cleanedTranscript}"`);
                    addDebugMessage(`From: "${oldText}"`);
                  }
                }
              }
            }
            
            if (cleanedTranscript) {
              // Mark that we've received speech content
              setHasSpeechContent(true);
              
              // Update the textarea directly to ensure it's reflected immediately
              if (textareaRef.current) {
                // NUCLEAR OPTION for Android: when we detect we're on Android,
                // ALWAYS replace the entire text area content rather than appending
                if (isAndroid?.current) {
                  // For Android, completely replace text box content to avoid cumulative repetition
                  // since Android keeps sending new complete transcripts
                  const existingText = textareaRef.current.value.trim();
                  const newTranscript = cleanedTranscript.trim();
                  
                  addDebugMessage(`NUCLEAR: Replace entire text`);
                  addDebugMessage(`Old: "${existingText.substr(0, 20)}..."`);
                  addDebugMessage(`New: "${newTranscript.substr(0, 20)}..."`);
                  
                  // Only replace if the new transcript is longer (Android usually builds up)
                  // or if the existing text is very short
                  if (newTranscript.length > existingText.length || existingText.length < 10) {
                    // Set the value directly in the textarea, completely replacing previous content
                    textareaRef.current.value = newTranscript;
                    console.log('🎤 [ANDROID] Completely replaced textarea with:', newTranscript);
                    
                    // Also update React state to keep things in sync
                    setMessage(newTranscript);
                  } else {
                    // If new text is shorter, stick with existing text
                    addDebugMessage(`Kept existing (longer) text`);
                  }
                } else {
                  // Normal behavior for non-Android devices - append as usual
                  const currentText = textareaRef.current.value;
                  let newMessage;
                  
                  if (!currentText) {
                    newMessage = cleanedTranscript;
                  } else if (currentText.endsWith(' ') || cleanedTranscript.startsWith(' ')) {
                    newMessage = currentText + cleanedTranscript;
                  } else {
                    newMessage = currentText + ' ' + cleanedTranscript;
                  }
                  
                  // Set the value directly in the textarea
                  textareaRef.current.value = newMessage;
                  console.log('🎤 Directly updated textarea to:', newMessage);
                  
                  // Also update React state to keep things in sync
                  setMessage(newMessage);
                }
                
                // Adjust textarea height
                adjustTextareaHeight();
              } else {
                // Fallback to just updating the state if textarea ref isn't available
                if (isAndroid?.current) {
                  // For Android, completely replace state to avoid accumulation
                  const newTranscript = cleanedTranscript.trim();
                  console.log('🎤 [ANDROID-STATE] Complete replacement with:', newTranscript);
                  setMessage(newTranscript);
                } else {
                  // Normal behavior for non-Android - append
                  setMessage(prev => {
                    // Ensure proper spacing between existing message and new content
                    let newMessage;
                    if (!prev) {
                      newMessage = cleanedTranscript;
                    } else if (prev.endsWith(' ') || cleanedTranscript.startsWith(' ')) {
                      newMessage = prev + cleanedTranscript;
                    } else {
                      newMessage = prev + ' ' + cleanedTranscript;
                    }
                    
                    console.log('🎤 Updated message state:', newMessage);
                    return newMessage;
                  });
                }
              }
              
              // Start silence detection after receiving content
              startSilenceDetection();
            } else {
              console.log('🎤 Empty transcript after cleaning, not updating message');
            }
          } else if (i === event.results.length - 1) {
            // For Android, also track interim results to handle word repetition
            if (isAndroid?.current) {
              previousTranscripts.current[i] = transcript;
              console.log('🎤 Android fix: Tracking interim result for index', i);
              addDebugMessage(`Tracking interim[${i}]: "${transcript.substr(0, 15)}..."`);
            }
            
            // Only log the most recent interim result to avoid spam
            console.log('🎤 Interim result (not adding to message yet):', transcript);
            
            // Even with interim results, update silence detection
            startSilenceDetection();
          }
        }
      };
      
      recognition.onerror = (event: any) => {
        console.error('🎤 Speech recognition error:', event.error);
        
        // Handle no-speech error specially in continuous mode
        if (continuousMode && event.error === 'no-speech') {
          console.log('🎤 No speech detected but in continuous mode, will continue listening');
          
          // We keep the recognition going, but don't set isListening to false
          // Instead, restart the recognition after a short delay
          setTimeout(() => {
            if (isListening) {
              try {
                // @ts-ignore
                if (window.recognitionInstance) {
                  // @ts-ignore
                  window.recognitionInstance.stop();
                  console.log('🎤 Stopped recognition to restart after no-speech error');
                }
                
                startSpeechRecognition();
                console.log('🎤 Restarted recognition after no-speech error');
              } catch (e) {
                console.error('🎤 Error restarting recognition after no-speech:', e);
                setIsListening(false);
              }
            }
          }, 300);
        } else if (event.error === 'not-allowed') {
          // Specific handling for microphone permission denied
          console.log('🎤 Microphone access denied');
          setToastMessage('Microphone access denied. Please check your browser permissions and ensure no other app is using the microphone.');
          setTimeout(() => setToastMessage(null), 8000); // Hide toast after 8 seconds
          setIsListening(false);
        } else {
          // For other errors, stop listening
          console.log('🎤 Stopping recognition due to error:', event.error);
          
          // Show appropriate message based on error type
          if (event.error === 'network') {
            setToastMessage('Network error occurred while using voice input. Please check your internet connection.');
          } else if (event.error === 'aborted') {
            setToastMessage('Voice recognition was aborted. Please try again.');
          } else if (event.error === 'audio-capture') {
            setToastMessage('No microphone was found or the microphone is not working properly.');
          } else if (event.error === 'service-not-allowed') {
            setToastMessage('Speech recognition service is not allowed. This might be a browser security restriction.');
          } else {
            setToastMessage(`Speech recognition error: ${event.error}. Please try again.`);
          }
          
          setTimeout(() => setToastMessage(null), 8000); // Hide toast after 8 seconds
          setIsListening(false);
        }
      };
      
      recognition.onend = () => {
        console.log('🎤 Speech recognition ended');
        
        // Restart recognition if still in listening mode or in continuous mode
        if (isListening) {
          console.log('🎤 Restarting speech recognition...');
          try {
            // Special handling for continuous mode
            if (continuousMode) {
              console.log('🎤 In continuous mode, restarting with fresh recognition instance');
              
              // Small delay before restart to avoid potential errors
              setTimeout(() => {
                if (isListening) {
                  startSpeechRecognition();
                }
              }, 300);
            } else {
              // Standard restart for normal mode
              recognition.start();
              console.log('🎤 Speech recognition restarted');
            }
          } catch (e) {
            console.error('🎤 Failed to restart speech recognition:', e);
            // Only turn off listening if not in continuous mode
            if (!continuousMode) {
              setIsListening(false);
            } else {
              // In continuous mode, try again with a longer delay
              setTimeout(() => {
                if (isListening) {
                  try {
                    startSpeechRecognition();
                    console.log('🎤 Retried starting speech recognition in continuous mode');
                  } catch (retryError) {
                    console.error('🎤 Failed to restart even after retry:', retryError);
                    setIsListening(false);
                  }
                }
              }, 1000);
            }
          }
        } else {
          console.log('🎤 Not restarting (listening mode off). isListening=', isListening, 'continuousMode=', continuousMode);
        }
      };
      
      // Store the recognition instance to stop it later
      try {
        // @ts-ignore
        window.recognitionInstance = recognition;
        recognition.start();
        console.log('🎤 Recognition started successfully');
      } catch (error) {
        console.error('🎤 Error starting speech recognition:', error);
        setIsListening(false);
        alert('Error starting speech recognition. Try again or check console for details.');
      }
    } else {
      console.error('🎤 Speech recognition is NOT supported in this browser');
      alert('Speech recognition is not supported in your browser.');
    }
  };
  
  // Function to completely stop speech recognition
  const forceStopSpeechRecognition = () => {
    console.log('🎤 Force stopping speech recognition...');
    
    // Clear any silence detection timeout
    if (silenceTimeoutId) {
      console.log('🎤 Clearing silence detection timeout');
      clearTimeout(silenceTimeoutId);
      setSilenceTimeoutId(null);
    }
    
    // @ts-ignore
    if (window.recognitionInstance) {
      try {
        console.log('🎤 Recognition instance found, forcing stop...');
        // @ts-ignore
        window.recognitionInstance.stop();
        console.log('🎤 Recognition stopped forcefully');
        
        try {
          // @ts-ignore
          window.recognitionInstance.abort();
          console.log('🎤 Recognition aborted');
        } catch (abortError) {
          console.log('🎤 Abort not available or error:', abortError);
        }
        
        // @ts-ignore
        window.recognitionInstance = null;
        console.log('🎤 Recognition instance cleared');
      } catch (e) {
        console.error('🎤 Error stopping speech recognition:', e);
      }
    } else {
      console.log('🎤 No recognition instance found to stop');
    }
    
    // Reset speech tracking state
    setLastSpeechTime(null);
    setHasSpeechContent(false);
    
    // Reset Android transcript tracking to prevent duplications in next session
    resetTranscriptTracking();
    
    // Set listening to false
    setIsListening(false);
    console.log('🎤 Listening state set to false (forced)');
  };
  
  // Regular stop function that respects continuous mode
  const stopSpeechRecognition = () => {
    console.log('🎤 Stopping speech recognition... continuousMode=', continuousMode);
    
    // If in continuous mode, don't fully stop, just pause briefly then restart
    if (continuousMode) {
      console.log('🎤 In continuous mode - will restart after brief pause');
      
      // Clear any silence detection timeout
      if (silenceTimeoutId) {
        console.log('🎤 Clearing silence detection timeout');
        clearTimeout(silenceTimeoutId);
        setSilenceTimeoutId(null);
      }
      
      // Reset speech tracking state
      setLastSpeechTime(null);
      setHasSpeechContent(false);
      
      // Reset Android transcript tracking
      resetTranscriptTracking();
      
      // Pause the current recognition instance
      // @ts-ignore
      if (window.recognitionInstance) {
        try {
          // @ts-ignore
          window.recognitionInstance.stop();
          console.log('🎤 Recognition paused (will restart for continuous mode)');
          
          // @ts-ignore
          window.recognitionInstance = null;
        } catch (e) {
          console.error('🎤 Error pausing speech recognition:', e);
        }
      }
      
      // Restart after a short pause, but keep listening state true
      setTimeout(() => {
        if (continuousMode) {
          console.log('🎤 Restarting in continuous mode after pause');
          startSpeechRecognition();
        }
      }, 1000);
    } else {
      // Not in continuous mode, do a complete stop
      forceStopSpeechRecognition();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-850 p-2 sm:p-4 prompt-form">
      {/* Debug Panel - only shown when debug mode is active */}
      {debugMode && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-100 p-2 text-xs font-mono overflow-y-auto" style={{ maxHeight: '40vh' }}>
          <div className="flex justify-between items-center mb-1">
            <h3 className="font-bold text-sm">Voice Recognition Debug</h3>
            <div className="flex gap-2">
              <button 
                onClick={() => {
                  // Show detailed information about the Web Speech API and current state
                  const detailedInfo = {
                    userAgent: navigator.userAgent,
                    speechSupport: !!window.SpeechRecognition || !!window.webkitSpeechRecognition,
                    androidDetected: isAndroid?.current,
                    previousTranscripts: previousTranscripts.current,
                    allResults: allFinalResults.current,
                    date: new Date().toISOString()
                  };
                  
                  // Create preformatted text for easier reading
                  const detailText = JSON.stringify(detailedInfo, null, 2);
                  
                  // Store in localStorage for persistent access
                  localStorage.setItem('speechDebugDump', detailText);
                  
                  // Add to debug messages with a prompt to check localStorage
                  addDebugMessage('DETAIL DUMP added to localStorage.speechDebugDump');
                }}
                className="px-2 py-1 bg-blue-200 rounded text-xs"
              >
                Details
              </button>
              <button 
                onClick={() => setDebugInfo([])} 
                className="px-2 py-1 bg-yellow-200 rounded text-xs"
              >
                Clear
              </button>
              <button 
                onClick={() => {
                  setDebugMode(false);
                  localStorage.setItem('interpretosVoiceDebug', 'false');
                }} 
                className="px-2 py-1 bg-red-200 rounded text-xs"
              >
                Hide
              </button>
            </div>
          </div>
          <div>
            {debugInfo.length > 0 ? (
              debugInfo.map((msg, idx) => (
                <div key={idx} className="border-b border-yellow-200 py-1">{msg}</div>
              ))
            ) : (
              <div className="py-1">Waiting for speech recognition activity...</div>
            )}
          </div>
        </div>
      )}
      {/* Toast Message */}
      {toastMessage && (
        <div className="absolute top-0 left-0 right-0 transform -translate-y-full mb-2 z-50 flex justify-center pointer-events-none">
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 rounded shadow-md pointer-events-auto max-w-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center mr-3">
                <svg className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{toastMessage}</span>
              </div>
              <button 
                onClick={() => setToastMessage(null)} 
                className="text-red-500 hover:text-red-700 focus:outline-none"
                aria-label="Close"
              >
                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="mx-auto prompt-container">
        {/* Attachments preview */}
        {(images.length > 0 || files.length > 0) && (
          <div className="flex gap-2 mb-3 pb-2 overflow-x-auto border-b dark:border-gray-700">
            {images.map((image, index) => (
              <div key={`img-${index}`} className="relative flex-shrink-0">
                <img
                  src={image}
                  alt={`Preview ${index + 1}`}
                  className="w-20 h-20 object-cover rounded-md shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => setImages(images.filter((_, i) => i !== index))}
                  className="absolute -top-2 -right-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors shadow-sm"
                >
                  ×
                </button>
              </div>
            ))}
            {files.map((file, index) => (
              <div key={`file-${index}`} className="relative flex-shrink-0">
                <div className="w-48 p-3 bg-gray-100 dark:bg-gray-800 rounded-md shadow-sm">
                  <div className="truncate text-sm font-medium dark:text-white">{file.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size)}
                    <span className="ml-2">{file.type || 'Unknown type'}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setFiles(files.filter((_, i) => i !== index))}
                  className="absolute -top-2 -right-2 bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors shadow-sm"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* Chat input area styled like ChatGPT */}
        <div className={`flex items-center bg-white dark:bg-gray-800 rounded-xl border ${
          isListening 
            ? 'border-red-400 dark:border-red-500 shadow-md' 
            : 'border-gray-300 dark:border-gray-600 hover:shadow'
        } transition-all w-full mx-auto`}>
          {/* Add button (plus icon) with dropdown menu */}
          <div className="relative">
            {/* Use state instead of hover for menu */}
            <button
              type="button"
              className="p-3 ml-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Add content"
              onClick={() => {
                // Add a menuOpen state
                setAttachMenuOpen(!attachMenuOpen);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
            </button>
            
            {/* Dropdown menu for attachments */}
            <div 
              ref={menuRef}
              className={`absolute bottom-full left-0 mb-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 w-48 py-1 ${attachMenuOpen ? 'block' : 'hidden'}`}>
              <button
                type="button"
                onClick={() => {
                  imageInputRef.current?.click();
                  setAttachMenuOpen(false);
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Image className="w-4 h-4" />
                <span>Upload image</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  fileInputRef.current?.click();
                  setAttachMenuOpen(false);
                }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Paperclip className="w-4 h-4" />
                <span>Attach file</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  // Check if we should only record taps for debug mode toggle
                  if (e.shiftKey) {
                    // If shift is held, only record tap for debug mode without starting speech
                    handleMicTap();
                    // Show a brief message when shift is used
                    const remainingTaps = 5 - Math.min(5, tapTimestamps.current.length);
                    if (remainingTaps > 0) {
                      alert(`Debug mode: ${remainingTaps} more tap${remainingTaps !== 1 ? 's' : ''} needed with Shift key`);
                    }
                  } else {
                    // Normal operation: toggle speech and record tap
                    toggleSpeechRecognition();
                    handleMicTap();
                  }
                  setAttachMenuOpen(false);
                }}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  isListening 
                    ? 'text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20' 
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Mic className="w-4 h-4" />
                <span>{isListening ? "Stop voice input" : "Voice input"}</span>
                <span className={`ml-auto text-xs ${isListening ? "text-red-500 dark:text-red-400 font-medium" : "text-gray-500 dark:text-gray-400"}`}>
                  {isListening ? "Say 'send' to submit" : ""}
                </span>
                {debugMode && <span className="ml-1 w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>}
              </button>
            </div>
          </div>
          
          {/* Textarea */}
          <div className="flex-1 min-w-0">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handleImagePaste}
              placeholder={isListening ? "Listening..." : "Enter a prompt for Interpretos"}
              className="w-full resize-none border-0 bg-transparent text-gray-900 dark:text-white py-3 px-2 focus:ring-0 focus:outline-none overflow-hidden"
              rows={1}
              style={{ maxHeight: '120px' }}
            />
          </div>
          
          {/* Action buttons - unified to prevent click race conditions */}
          <div className="flex items-center pr-2">
            {/* Single button that changes behavior based on isLoading state */}
            {/* Using a single button prevents race conditions during click */}
            {/* CRITICAL: type="button" prevents form submission on click */}
            <button
              type="button"
              className={`ml-2 p-2 rounded-full flex items-center justify-center ${
                isLoading
                  ? 'bg-red-500 dark:bg-red-600 text-white hover:bg-red-600 dark:hover:bg-red-700'
                  : 'bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700'
              }`}
              title={isLoading ? "Cancel" : (message.trim() || images.length > 0 || files.length > 0 ? "Send message" : "Start voice input")}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (isLoading) {
                  // Cancel the request
                  onCancel();
                } else if (message.trim() || images.length > 0 || files.length > 0) {
                  // Submit the form manually
                  const form = e.currentTarget.closest('form');
                  if (form) {
                    form.requestSubmit();
                  }
                } else {
                  // Toggle speech recognition (mic button behavior)
                  toggleSpeechRecognition();
                  handleMicTap();
                }
              }}
            >
              {isLoading ? (
                <XCircle className="w-5 h-5" />
              ) : message.trim() || images.length > 0 || files.length > 0 ? (
                <Send className="w-5 h-5" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
        
        {/* Send key and voice hints */}
        <div className="mt-1 mb-1 text-xs text-center text-gray-500 dark:text-gray-400 mx-auto max-w-xs">
          {isListening ? (
            <div>
              <span className="text-red-500 dark:text-red-400 font-medium animate-pulse">
                Listening... Speak then pause for 2.5 seconds to auto-submit
              </span>
              {/* Show Android status only in debug mode */}
              {debugMode && isAndroid?.current && (
                <span className="ml-2 text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                  Android optimized
                </span>
              )}
              <div className="mt-1">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={continuousMode}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setContinuousMode(newValue);
                      console.log(`🎤 Continuous mode ${newValue ? 'enabled' : 'disabled'}`);
                      
                      // If enabling, make sure recognition will restart
                      // If disabling, don't do anything special
                    }}
                    className="h-3 w-3 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-1 text-xs text-gray-600 dark:text-gray-300">
                    Continuous conversation mode <span className="text-amber-500 font-medium">(experimental)</span>
                  </span>
                  {continuousMode && (
                    <span className="ml-1 text-xs text-blue-500 dark:text-blue-400">
                      (active)
                    </span>
                  )}
                </label>
              </div>
            </div>
          ) : (
            <span>
              {sendKey === 'Enter' ? 'Press Enter to send, Shift+Enter for new line' : 'Press Ctrl+Enter to send, Enter for new line'}
              {/* Show Android status only in debug mode */}
              {debugMode && isAndroid?.current && (
                <span className="ml-2 text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                  Android voice input optimized
                </span>
              )}
            </span>
          )}
        </div>
        
        {/* Hidden file inputs */}
        <input
          type="file"
          ref={imageInputRef}
          className="hidden"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              const reader = new FileReader();
              reader.onload = (e) => {
                setImages((prev) => [...prev, e.target?.result as string]);
              };
              reader.readAsDataURL(file);
            }
            // Reset the input value so the same file can be selected again
            e.target.value = '';
          }}
        />
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          multiple
          onChange={(e) => {
            handleFileSelect(e.target.files);
            // Reset the input value so the same files can be selected again
            e.target.value = '';
          }}
        />
      </div>
    </form>
  );
}
